import { IconPlugConnectedX, IconServerOff } from "@tabler/icons-react";

import { ApiError } from "../api/client";
import { SUPPORT_CHANNEL } from "../config";

/**
 * True when an error means "there is no truth to show", not "this failed".
 *
 * Args:
 *     error: Anything thrown by a query.
 *
 * Returns:
 *     True for `503 not_connected` and `502 cluster_unavailable`.
 */
export function isClusterProblem(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    (error.isNotConnected || error.isClusterUnavailable)
  );
}

interface ClusterProblemProps {
  error: ApiError;
  onRetry?: () => void;
}

interface Copy {
  title: string;
  lead: string;
  checklistTitle: string;
  checklist: [string, string][];
}

const NOT_CONNECTED: Copy = {
  title: "The console cannot see the machines",
  lead:
    "It has not been told which machines to manage, so it has nothing to " +
    "show. This is a setting on the console's side, not something you did and " +
    "not something you can fix — the platform team can, in a minute. Nothing " +
    "of yours is lost, and no GPU is being wasted.",
  checklistTitle: "For the platform team",
  checklist: [
    [
      "KUBECONFIG",
      "path to a kubeconfig file — leave it empty when the console runs inside the cluster.",
    ],
    ["K8S_NAMESPACE", "where workspaces are created (default gpu-workspaces)."],
    [
      "SCW_SECRET_KEY, SCW_CLUSTER_ID, SCW_POOLS",
      "so the console can start a machine when none is free — SCW_POOLS is " +
        "also how it knows which machines belong to research.",
    ],
    [
      "K8S_NODE_SELECTOR",
      "optional override of which machines belong to research; leave it " +
        "empty to derive them from SCW_POOLS.",
    ],
    [
      "PROMETHEUS_URL",
      "optional — without it, how busy each GPU is stays unknown rather than guessed.",
    ],
  ],
};

const UNAVAILABLE: Copy = {
  title: "The console cannot reach the machines right now",
  lead:
    "It knows which machines to manage but could not talk to them just now. " +
    "Nothing is lost: workspaces that are running keep running, and this page " +
    "recovers on its own as soon as the connection is back. If it lasts more " +
    "than a few minutes, it is worth telling the platform team.",
  checklistTitle: "For the platform team",
  checklist: [
    ["Network", "can the console reach the Kubernetes API and the Scaleway API?"],
    ["Credentials", "is the service account token or the Scaleway API key still valid?"],
    ["Scaleway", "is the account or the Kubernetes product blocked (403 on every call)?"],
  ],
};

/**
 * What a researcher sees when there is no measured truth to show.
 *
 * It leads with the two things they need — this is not your fault, and here is
 * who fixes it — and keeps the environment variables behind a fold. A wall of
 * configuration is the deployer's screen, and the audience here is a research
 * scientist (`docs/CONVENTIONS.md`).
 *
 * Args:
 *     error: The API error that caused it.
 *     onRetry: Optional retry action.
 *
 * Returns:
 *     A full-width explanatory panel.
 */
export function ClusterProblem({ error, onRetry }: ClusterProblemProps): JSX.Element {
  const copy = error.isNotConnected ? NOT_CONNECTED : UNAVAILABLE;
  const Icon = error.isNotConnected ? IconPlugConnectedX : IconServerOff;
  const details = Object.entries(error.details ?? {});

  return (
    <section className="problem" role="alert" aria-labelledby="problem-title">
      <div className="problem__head">
        <Icon size={20} aria-hidden="true" />
        <h2 className="problem__title" id="problem-title">
          {copy.title}
        </h2>
      </div>

      <p className="problem__lead">{copy.lead}</p>

      <p className="problem__lead">
        Who to tell: {SUPPORT_CHANNEL}.
      </p>

      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          Check again
        </button>
      ) : null}

      <details className="problem__details-block">
        <summary>{copy.checklistTitle}</summary>

        <div className="problem__quote">
          <span className="problem__quote-label">The server said</span>
          <p className="problem__quote-text">{error.message}</p>
          <p className="problem__quote-code mono">
            HTTP {error.status} · {error.code}
          </p>
          {details.length > 0 ? (
            <dl className="problem__details mono">
              {details.map(([key, value]) => (
                <div key={key} className="problem__detail">
                  <dt>{key}</dt>
                  <dd>{typeof value === "string" ? value : JSON.stringify(value)}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>

        <ul className="problem__list">
          {copy.checklist.map(([name, explanation]) => (
            <li key={name} className="problem__item">
              <span className="mono problem__key">{name}</span>
              <span className="problem__explanation">{explanation}</span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
